/**
 * Returns the architect's "what's next" context for a given project stage.
 * Used by both the project detail page (banner) and the project list (next-
 * action column) so the language stays consistent.
 *
 * Output shape:
 *   {
 *     who:       "you" | "owner" | "city" | "system",
 *     tone:      "action" | "waiting" | "done",
 *     headline:  short imperative ("Open Architect Workspace") OR status ("Waiting on owner")
 *     blurb:     one-line explanation of why
 *     cta:       optional { label, to } for a primary button
 *   }
 */
export function projectNextAction(project) {
  if (!project) return null;
  const stage = project.current_stage;
  const consultId = project.consultation_id;
  switch (stage) {
    case "Selection":
      return {
        who: "owner",
        tone: "waiting",
        headline: "Waiting on owner",
        blurb:
          "The owner is finalizing site selection. The project will move into Consultation once they request your input.",
        cta: consultId
          ? { label: "Open consultation", to: `/architect/inquiries/${consultId}` }
          : null,
      };
    case "Consultation":
      return {
        who: "you",
        tone: "action",
        headline: "Your input needed",
        blurb:
          "Respond to the consultation thread, share materials, and align on the build scope with the owner.",
        cta: consultId
          ? { label: "Open consultation", to: `/architect/inquiries/${consultId}` }
          : null,
      };
    case "Purchase":
      return {
        who: "owner",
        tone: "waiting",
        headline: "Waiting on owner",
        blurb:
          "The owner is purchasing plans. The project moves into Expedited Review once they complete payment.",
      };
    case "Expedited Review":
      return {
        who: "city",
        tone: "waiting",
        headline: "City review in progress",
        blurb:
          "DMD / DBNS are reviewing the permit application. You'll be pinged if they request revisions.",
      };
    case "Build":
      return {
        who: "owner",
        tone: "waiting",
        headline: "Owner is hiring",
        blurb:
          "The owner is selecting a contractor and prepping the site. The project moves into Construction Inspection once foundation pour is scheduled.",
      };
    case "Construction Inspection":
      return {
        who: "you",
        tone: "action",
        headline: "Your turn — log inspections",
        blurb:
          "Verify each milestone (Foundation → Rough-in → Final), upload site photos, then Sign & Submit DBNS to close the project.",
        cta: {
          label: "Open Architect Workspace",
          to: `/architect/projects/${project.id}/construction`,
        },
      };
    case "Complete":
      return {
        who: "system",
        tone: "done",
        headline: "Project complete",
        blurb:
          "Final inspection report submitted to DBNS. No further action required.",
      };
    default:
      return null;
  }
}
